import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  InlineLoading,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@carbon/react";
import { DocumentAdd } from "@carbon/icons-react";
import { useFileInputUploader } from "../../hooks/useFileInputUploader";
import { useUpdateDeliveryFileUrl } from "../../hooks/useDeliveries";
import { useNotification } from "../../context";
import "./deliveriesFileUploadModal.scss";

export interface DeliveriesFileUploadModalProps {
  /** The delivery being edited, or `null` when the dialog is closed. `ComposedModal`'s own
   * `open` is derived from this rather than tracked separately, so there is one source of truth
   * for "which row's dialog is open" — `DeliveriesTable.tsx`'s own `useState<string | null>`. */
  deliveryId: string | null;
  onClose: () => void;
}

/**
 * Replaces `DeliveriesTableRow.tsx`'s ad hoc `Dialog`/`Transition` (Headless UI) file-upload
 * panel with Carbon's `ComposedModal` chrome, following `ImportFromFileModal.tsx`'s own disclosed
 * pattern: a native `<label htmlFor>` + real `<input type="file">` rather than Carbon's
 * `FileUploader` (whose own label is the button caption, not an externally-associated field
 * label). Upload-then-confirm is the same two-step flow the original had: `useFileInputUploader`
 * uploads to S3 and returns a URL, `useUpdateDeliveryFileUrl` (unchanged) persists it on the
 * delivery once the user clicks Accept.
 */
const DeliveriesFileUploadModal = ({
  deliveryId,
  onClose,
}: DeliveriesFileUploadModalProps) => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const [fileName, setFileName] = useState("");

  const {
    data: uploadedFileUrl,
    mutate: uploadFile,
    isPending: isUploadingFile,
    reset: resetUpload,
  } = useFileInputUploader();

  const { mutate: updateDeliveryFileUrl, isPending: isUpdatingDelivery } =
    useUpdateDeliveryFileUrl(() => {
      openNotification(t("deliveries.uploadSuccess"));
      handleClose();
    });

  const handleClose = () => {
    setFileName("");
    resetUpload();
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    uploadFile(file);
  };

  const handleConfirm = () => {
    if (!deliveryId || !uploadedFileUrl) return;
    updateDeliveryFileUrl({ deliveryId, fileUrl: uploadedFileUrl });
  };

  const busy = isUploadingFile || isUpdatingDelivery;
  const title = t("deliveries.form.upload");

  return (
    <ComposedModal
      open={Boolean(deliveryId)}
      aria-label={title}
      onClose={handleClose}
    >
      <ModalHeader title={title} />
      <ModalBody hasForm>
        <div className="deliveries-upload-modal__field">
          <label htmlFor="delivery-file-upload" className="cds--label">
            {title}
          </label>
          <input
            id="delivery-file-upload"
            type="file"
            onChange={handleFileChange}
            disabled={busy}
            className="deliveries-upload-modal__input"
          />
          {fileName && (
            <p className="deliveries-upload-modal__filename">
              <DocumentAdd size={16} />
              {fileName}
            </p>
          )}
        </div>

        {isUploadingFile && (
          <InlineLoading description={t("deliveries.updating")} />
        )}
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={handleClose}>
          {t("cta.cancel")}
        </Button>
        <Button onClick={handleConfirm} disabled={busy || !uploadedFileUrl}>
          {busy ? t("deliveries.updating") : t("cta.accept")}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};

export default DeliveriesFileUploadModal;
